// The envelope every /api response wears, and the closed vocabulary of refusals.
//
// ONE SHAPE, EVERY ROUTE. Success is `{ data: T }`; failure is
// `{ error: { code, message, details? } }` with the HTTP status the code maps
// to. A client writes the parse once and branches on `code`; nothing has to
// read a status number to know what happened, and nothing has to read English
// to know what to do.
//
// The codes are CLOSED on the wire — a caller may switch on them exhaustively
// — which is the opposite of the rule for the enums inside a view, where a
// value added next season must not fail an installed client's parse. The
// difference is deliberate: a new refusal reason is a new contract, and a
// client that has never heard of it cannot handle it correctly by guessing.
// Anything that would want a new code carries its specifics in `details`
// instead.
//
// ONE VOCABULARY, AND THE SOCKET USES IT TOO. Until 2026-09-17 there was a
// second closed enum — ICD-U01's `RefusalCode`, eleven hyphenated members to
// this one's nine underscored ones — for the same concept on the realtime
// side. Two enums for one thing means a client carries both plus a mapping
// table, and the two drift the first time only one of them is extended. It is
// deleted; `intent.rejected` on a socket carries an `ErrorCode` and the same
// `details`. See docs/decisions/2026-09-17-the-draft-socket-is-same-origin.md.

import { z } from 'zod';

export const ErrorCode = z.enum([
  'unauthorized',
  'forbidden',
  'not_found',
  'invalid',
  'conflict',
  'locked',
  'method_not_allowed',
  'rate_limited',
  'unavailable',
]);
export type ErrorCode = z.infer<typeof ErrorCode>;

/**
 * One thing wrong with one field.
 *
 * `path` is dotted from the root of the request body — `colors.primary`, or
 * the empty string when the whole body is wrong. A client highlights the
 * field it knows and shows `message` for the rest, which is what makes
 * field-level feedback possible without re-implementing every constraint in
 * a second language. Added 2026-09-17 (audit 3.4): an `invalid` response used
 * to carry `issues[0].message` and nothing else.
 */
export const ApiIssue = z.object({
  path: z.string(),
  message: z.string(),
});
export type ApiIssue = z.infer<typeof ApiIssue>;

/**
 * The machine-readable half of a refusal.
 *
 * Open by design — a route may add a key without needing a new `ErrorCode`
 * and without breaking anybody's parse. `issues` and `reason` are the two
 * keys with a fixed meaning.
 */
export const ApiErrorDetails = z.looseObject({
  issues: z.array(ApiIssue).optional(),
  /**
   * Which refusal of this code it was, when the code alone is too coarse.
   *
   * OPEN where `code` is closed, and that asymmetry is the whole mechanism:
   * a client switches exhaustively on `code`, and treats an unfamiliar
   * `reason` as the code with no further detail, rendering `message`. So a
   * new reason is added freely and breaks nobody, which is the opposite of
   * adding a code.
   *
   * Added 2026-09-17 when `RefusalCode` was folded into `ErrorCode` (decision
   * A11). That enum had eleven members to this one's nine, and five of them —
   * `illegal-roster`, `not-eligible`, `insufficient-budget`, `not-your-turn`
   * and `already-done` — were not refusal *kinds* at all but reasons for one.
   * They live here now:
   *
   *   illegal-roster       → invalid   · reason: 'illegal-roster'
   *   not-eligible         → invalid   · reason: 'not-eligible'
   *   insufficient-budget  → invalid   · reason: 'insufficient-budget'
   *   not-your-turn        → conflict  · reason: 'not-your-turn'
   *   already-done         → conflict  · reason: 'already-done'
   *
   * The other six mapped one to one, modulo spelling: `not-found` is
   * `not_found`, and `locked`, `forbidden`, `conflict`, `invalid` and
   * `unavailable` were already the same word in both.
   */
  reason: z.string().optional(),
});
export type ApiErrorDetails = z.infer<typeof ApiErrorDetails>;

export const ApiError = z.object({
  code: ErrorCode,
  /** Written for the person, in their language, safe to show. */
  message: z.string(),
  details: ApiErrorDetails.optional(),
});
export type ApiError = z.infer<typeof ApiError>;

export function envelope<T extends z.ZodTypeAny>(data: T) {
  return z.union([z.object({ data }), z.object({ error: ApiError })]);
}

export const HTTP_STATUS: Record<ErrorCode, number> = {
  unauthorized: 401,
  forbidden: 403,
  not_found: 404,
  invalid: 400,
  conflict: 409,
  locked: 409,
  // 405 needs `Allow` on the response, which `route()` supplies. It is its own
  // code rather than a 400 because the request was understood and the path
  // exists — a client that gets it has a bug in its method, not in its body.
  method_not_allowed: 405,
  rate_limited: 429,
  unavailable: 503,
};

/**
 * A Zod failure as a refusal.
 *
 * The lead sentence is the first issue's message, because that is what a
 * screen shows above the form; every issue is in `details.issues`, because
 * that is what a form highlights. One helper, so the two can never disagree
 * and no route invents a third format.
 */
export function invalidFrom(error: z.ZodError, fallback = 'Check that and try again.'): ApiError {
  const issues = error.issues.map((i) => ({
    path: i.path.map((p) => String(p)).join('.'),
    message: i.message,
  }));
  return {
    code: 'invalid',
    message: issues[0]?.message ?? fallback,
    details: { issues },
  };
}

// ── Paging ────────────────────────────────────────────────────────────────

/**
 * One page of anything.
 *
 * ONE SHAPE FOR THE WHOLE SURFACE. There were three before 2026-09-17:
 * `DirectoryPage.players`, `PlayersPage.rows`, and `/api/admin/reviews`
 * silently cutting itself off at 200 rows with nothing in the body to say so
 * (audit 3.5). A client that can page one list can now page all of them.
 *
 * `total` is optional because counting is a second query and not every list
 * is worth one. `truncated` is the honest answer when a route has a hard cap
 * and no cursor: there is more, and this is not the way to reach it.
 */
export function pageOf<T extends z.ZodTypeAny>(item: T) {
  return z.object({
    items: z.array(item),
    /** Pass back as `?cursor=`. Null when this is the last page. */
    nextCursor: z.string().nullable(),
    /** How many match, ignoring the page. Absent when nobody counted. */
    total: z.number().int().min(0).optional(),
    /** True when rows were dropped and no cursor will reach them. */
    truncated: z.boolean().optional(),
  });
}

export interface Page<T> {
  items: T[];
  nextCursor: string | null;
  total?: number;
  truncated?: boolean;
}

/**
 * A cursor is OPAQUE, and it is bound to the filter it was minted under.
 *
 * The directory's cursor was `lastName|id` in the clear until 2026-09-17,
 * which had two costs. It was constructible — a caller could invent one and
 * page from an arbitrary name, which is harmless on a public list and would
 * not be on one scoped to a person. And it was not tied to its query, so a
 * cursor taken from `?q=ma` and replayed against `?q=jo` answered **200 with
 * the wrong page**: the rows after "Mahomes" among the Johnsons, with nothing
 * in the response to say anything had gone wrong (audit 3.5).
 *
 * So the fingerprint of the filter travels inside the cursor and is checked
 * on the way back in. A mismatch is `invalid` — start the walk again — rather
 * than a quietly different answer.
 *
 * Hex of UTF-8, not base64: `btoa` throws on any character above U+00FF and a
 * surname is exactly where one turns up. Opaque here means "not yours to
 * construct", not "encrypted"; it is a sort key, and knowing it buys nothing
 * the sort order does not already hand out.
 */
const CURSOR_PREFIX = 'c1';

/**
 * Unit separator. Never in a name, never in an id, so it cannot be smuggled.
 *
 * `String.fromCharCode` rather than an escape in a string literal: Prettier
 * rewrites the escape into the literal control character, which is then
 * invisible in every editor and survives exactly as long as nobody
 * copy-pastes the line.
 */
const SEP = String.fromCharCode(31);

function toHex(text: string): string {
  let out = '';
  for (const byte of new TextEncoder().encode(text)) out += byte.toString(16).padStart(2, '0');
  return out;
}

function fromHex(hex: string): string | null {
  if (hex.length % 2 !== 0 || !/^[0-9a-f]*$/.test(hex)) return null;
  const bytes = new Uint8Array(hex.length / 2);
  for (let i = 0; i < bytes.length; i++)
    bytes[i] = Number.parseInt(hex.slice(i * 2, i * 2 + 2), 16);
  return new TextDecoder().decode(bytes);
}

/**
 * A stable 32-bit fingerprint of whatever identifies the filter.
 *
 * FNV-1a. Not a security property and not trying to be: it exists so a cursor
 * from one query is refused by another, and a collision costs one wrong page
 * rather than anything unsafe.
 */
export function filterFingerprint(parts: readonly (string | number | boolean | null)[]): string {
  let hash = 0x811c9dc5;
  const text = parts.map((p) => (p === null ? '' : String(p))).join(SEP);
  for (const char of text) {
    hash ^= char.codePointAt(0) ?? 0;
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return hash.toString(16).padStart(8, '0');
}

export function encodeCursor(fingerprint: string, key: string): string {
  return CURSOR_PREFIX + toHex(`${fingerprint}${SEP}${key}`);
}

/**
 * The sort key inside a cursor, or why it cannot be used.
 *
 * `'stale'` means the cursor is well formed but was minted under a different
 * filter — a real and recoverable mistake, and worth its own answer.
 */
export function decodeCursor(
  cursor: string,
  fingerprint: string,
): { ok: true; key: string } | { ok: false; reason: 'malformed' | 'stale' } {
  if (!cursor.startsWith(CURSOR_PREFIX)) return { ok: false, reason: 'malformed' };
  const text = fromHex(cursor.slice(CURSOR_PREFIX.length));
  if (text === null) return { ok: false, reason: 'malformed' };
  const at = text.indexOf(SEP);
  if (at === -1) return { ok: false, reason: 'malformed' };
  if (text.slice(0, at) !== fingerprint) return { ok: false, reason: 'stale' };
  return { ok: true, key: text.slice(at + 1) };
}
